import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiRequestError, GatewayUnavailableError, GATEWAY_QUERY_URL } from '../api';
import { useSession } from '../auth/useSession';
import { ErrorState, LoadingState } from '../components/states';

type HealthState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; data: unknown }
  | { status: 'error'; message: string; code?: string };

/**
 * Smoke-страница: дергает view.health через query-шлюз и показывает
 * фактический ответ или ошибку.
 *
 * view.health требует сессии (D3): без bearer шлюз всегда вернёт
 * unauthenticated. Поэтому пробу запускаем ТОЛЬКО под сессией; аноним видит
 * честную пометку «требует входа», а не гарантированную ошибку. Пока шлюзов
 * нет — под сессией покажет ошибку сети, это ожидаемо.
 */
export function HealthPage() {
  const session = useSession();
  const authenticated = session.token !== null;
  const [state, setState] = useState<HealthState>({ status: 'idle' });

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const { data } = await api.query<unknown>('view.health');
      setState({ status: 'ok', data });
    } catch (e) {
      if (e instanceof ApiRequestError) {
        setState({ status: 'error', message: e.message, code: e.code });
      } else if (e instanceof GatewayUnavailableError) {
        setState({ status: 'error', message: e.message });
      } else {
        setState({ status: 'error', message: 'Неизвестная ошибка' });
      }
    }
  }, []);

  useEffect(() => {
    if (authenticated) void load();
  }, [authenticated, load]);

  return (
    <div style={{ padding: 'var(--sp-8)', maxWidth: 720 }}>
      <h1 className="t-page-title">Health</h1>
      <p className="t-secondary" style={{ marginTop: 'var(--sp-2)' }}>
        Query-шлюз: <span className="t-code">{GATEWAY_QUERY_URL}</span> · операция{' '}
        <span className="t-code">view.health</span> (требует сессии)
      </p>
      <div style={{ marginTop: 'var(--sp-5)' }}>
        {!authenticated && (
          <div className="state" data-state="requires-auth">
            <div className="state__title">Проба требует входа</div>
            <div className="state__text">
              Операция <span className="t-code">view.health</span> доступна только
              аутентифицированной сессии. Войдите, чтобы запустить smoke-пробу шлюза.
            </div>
            <Link className="btn btn--secondary" to="/login">
              Перейти к входу
            </Link>
          </div>
        )}
        {authenticated && state.status === 'loading' && <LoadingState lines={3} />}
        {authenticated && state.status === 'ok' && (
          <pre
            className="tabular-nums"
            style={{
              fontSize: 12,
              background: 'var(--c-quiet-1)',
              border: '1px solid var(--c-border-2)',
              padding: 'var(--sp-4)',
              overflowX: 'auto',
            }}
          >
            {JSON.stringify(state.data, null, 2)}
          </pre>
        )}
        {authenticated && state.status === 'error' && (
          <ErrorState
            title="Шлюз не ответил успехом"
            text={state.code !== undefined ? `${state.code}: ${state.message}` : state.message}
            onRetry={() => void load()}
          />
        )}
      </div>
    </div>
  );
}
